import React from 'react';

import * as Form from 'components/form';
import * as Page from 'components/page';
import { useConversionOptions } from 'hooks/useConversionTable';

import { StartupCard } from '../components';
import * as Styled from './StyledCorporateStep2Add';

export function CorporateStep2AddForm(props) {
    const { form, skip, setSkip, onChange, onSubmit, openModal, dateError } = props;
    const { vars, calculateContractPoints, checkStartupCountry } = props.useRankingCorporate;
    const { ranking, contractPoints } = vars;

    const convValue = useConversionOptions(['contractValue'], { namespace: 'currencyUSD' });
    const convType = useConversionOptions(['contractType']);

    React.useEffect(() => {
        const { startDate, endDate, formalized, valueRange, contractType } = form.values;
        if (form.values.startup) {
            calculateContractPoints({
                startDate,
                endDate,
                formalized,
                valueRange,
                type: contractType,
                selectedStartup: form.values.startup,
            });
        }
    }, [form.values, calculateContractPoints]);

    React.useEffect(() => {
        if (form.values.startup) {
            checkStartupCountry(form.values.startup);
        }
    }, [form.values.startup, checkStartupCountry]);

    const currentDate = new Date();
    const currentYear = currentDate.getUTCFullYear();
    const currentMonth =
        String(currentDate.getUTCMonth() + 1).length === 1
            ? `0${currentDate.getUTCMonth() + 1}`
            : String(currentDate.getUTCMonth() + 1);
    const currentDay =
        String(currentDate.getUTCDate()).length === 1
            ? `0${currentDate.getUTCDate()}`
            : String(currentDate.getUTCDate());

    const contractTypeOptions = [
        { name: props.t('glossary:selectOption'), value: '' },
        ...convType.contractType.filter(type => type.value !== 'none'),
    ];

    // Create form fields object
    const fields = {
        startup: {
            error: form.errors.startup,
            label: props.t('glossary:startup'),
            name: 'startup',
            type: 'Startup',
            value: form.values.startup,
            onChange: ev => form.handleChange(ev, onChange.startup),
            required: !form.values.startupName,
        },

        otherContact: {
            error: form.errors.otherContact,
            label: props.t('glossary:otherContact'),
            name: 'otherContact',
            type: 'YesNo',
            value: form.values.otherContact,
            onChange: form.handleChange,
        },

        startupName: {
            error: form.errors.startupName,
            label: props.t('glossary:startupName'),
            name: 'startupName',
            type: 'text',
            value: form.values.startupName,
            onChange: form.handleChange,
        },
        contactName: {
            error: form.errors.contactName,
            label: props.t('glossary:contactNameContractStartup'),
            name: 'contactName',
            type: 'text',
            value: form.values.contactName,
            onChange: form.handleChange,
        },
        contactEmail: {
            error: form.errors.contactEmail,
            label: props.t('glossary:contactEmailContractStartup'),
            name: 'contactEmail',
            type: 'email',
            value: form.values.contactEmail,
            onChange: form.handleChange,
        },
        contractType: {
            error: form.errors.contractType,
            label: props.t('glossary:contractType'),
            name: 'contractType',
            type: 'select',
            options: contractTypeOptions,
            value: form.values.contractType,
            onChange: form.handleChange,
            icon: (
                <button className="ico-btn" type="button" onClick={openModal}>
                    <span
                        className="icon fa-fw fas fa-info-circle"
                        aria-hidden="true"
                        style={{ color: 'var(--primary)', cursor: 'pointer' }}
                    />
                </button>
            ),
        },
        contractDescription: {
            error: form.errors.contractDescription,
            label: props.t('glossary:contractDescription'),
            name: 'contractDescription',
            maxLength: 500,
            type: 'textarea',
            value: form.values.contractDescription,
            onChange: form.handleChange,
        },
        startDate: {
            displayFormat: 'MM-YYYY',
            error: form.errors.startDate,
            label: props.t('glossary:startDate'),
            maxLength: 4,
            name: 'startDate',
            min: `${String(currentYear - 10)}-${currentMonth}-${currentDay}`,
            max: form.values.endDate || `${String(currentYear)}-${currentMonth}-${currentDay}`,
            type: 'date',
            value: form.values.startDate,
            onChange: ev => form.handleChange(ev, onChange.date),
        },
        endDate: {
            displayFormat: 'MM-YYYY',
            error: form.errors.endDate,
            label: props.t('glossary:endDate'),
            maxLength: 4,
            name: 'endDate',
            min:
                form.values.startDate || `${String(currentYear - 2)}-${currentMonth}-${currentDay}`,
            max: `${String(currentYear + 10)}-${currentMonth}-${currentDay}`,
            type: 'date',
            value: form.values.endDate,
            onChange: ev => form.handleChange(ev, onChange.date),
        },
        formalized: {
            error: form.errors.formalized,
            label: props.t('glossary:isContractSignedCorps'),
            name: 'formalized',
            type: 'YesNo',
            required: true,
            value: form.values.formalized,
            onChange: form.handleChange,
        },
        valueRange: {
            error: form.errors.valueRange,
            label: props.t('glossary:contractValueRange'),
            name: 'valueRange',
            type: 'SelectRangeArray',
            options: convValue.contractValue,
            required: true,
            value: form.values.valueRange,
            onChange: form.handleChange,
            onlyIf: form.values.formalized === 'true',
        },
        isUpToDate: {
            error: form.errors.isUpToDate,
            label: props.t('glossary:profileDataIsUpToDate'),
            name: 'isUpToDate',
            type: 'checkbox',
            required: true,
            value: form.values.isUpToDate,
            onChange: form.handleChange,
        },
        isSecret: {
            error: form.errors.isSecret,
            label: props.t('glossary:isSecret'),
            name: 'isSecret',
            type: 'checkbox',
            required: false,
            value: form.values.isSecret,
            onChange: form.handleChange,
        },
    };

    const onClick = (ev, currValue) => {
        setSkip(true);
        form.addValues({ startup: {}, startupName: currValue });
        return true;
    };

    // Handle form submition
    const evSubmit = ev => {
        if (!skip && !form.values.startup) {
            ev.persist();
            ev.preventDefault();
            form.emitError({ name: 'startup', error: props.t('glossary:startupSelect') });
            return false;
        }
        return form.handleSubmit(ev, onSubmit);
    };
    return (
        <Form.Form name="CorporateStep2Add" onSubmit={evSubmit}>
            <Form.Field {...fields.startup}>
                <Page.LWTXT onClick={onClick}>
                    <span className="link">{props.t('CorporateStep2Add.skipStartup')}</span>
                </Page.LWTXT>
            </Form.Field>

            {(skip || form.values.startup) && (
                <Page.Boxed style={{ marginTop: '0' }}>
                    {skip && (
                        <>
                            <Page.Texts style={{ marginTop: '0' }}>
                                {props.t('CorporateStep2Add.skipStartupText')}
                            </Page.Texts>
                            <Form.Field {...fields.startupName} />
                        </>
                    )}
                    {!!Object.keys(form.values.startup).length && (
                        <>
                            <StartupCard {...form.values} />
                            <Form.Field {...fields.otherContact} />
                        </>
                    )}
                    {(skip || form.values.otherContact === 'true') && (
                        <>
                            <Form.Field {...fields.contactName} />
                            <Form.Field {...fields.contactEmail} />
                        </>
                    )}
                </Page.Boxed>
            )}

            <Page.Ruler />

            <Styled.StyledFields>
                <Form.Field {...fields.contractType} />
                <Form.Field {...fields.contractDescription} />
            </Styled.StyledFields>

            <Form.Row>
                <Form.Field size="6" {...fields.startDate} />
                <Form.Field size="6" {...fields.endDate} />
                {dateError && (
                    <Styled.DateError className="date-error">
                        {props.t('StartupStep8Add.dateError')}
                    </Styled.DateError>
                )}
            </Form.Row>

            <Page.Ruler />

            <Form.Field {...fields.formalized} />
            <Form.Field {...fields.valueRange} />

            <Page.Ruler />

            <span>
                {props.t('StartupStep8Add.maximumScore')} {ranking.year}:{' '}
                <b>
                    {contractPoints} {props.t('StartupStep8Add.points')}
                </b>
            </span>

            <Page.Ruler />

            <Form.Field {...fields.isSecret} />
            <Form.Field {...fields.isUpToDate} />

            <Form.Submt {...form} text={props.t('glossary:continue')} />
        </Form.Form>
    );
}
export default CorporateStep2AddForm;
